/**
 * RouteLogic-sectie: routekaart (vandaag of gekozen dag) + omzetprognose vooruit.
 *
 * Vaste sectie, onafhankelijk van het periode-filter: de prognose gaat per
 * definitie over de toekomst. Data komt uit /api/schoorsteenveger/routes
 * (server-side gecachet; het dashboard praat nooit rechtstreeks met RouteLogic).
 *
 * Kaart-lessen uit de call-tracking-map hergebruikt: integer zoom (rastertiles
 * nooit herschalen), CartoDB-tiles, achtergrond in tile-kleur.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import InfoDot from './InfoDot';

// Stabiel kleurenpalet per chauffeur (volgorde van eerste verschijning)
const DRIVER_COLORS = ['#2563eb', '#D02E17', '#059669', '#d97706', '#7c3aed', '#0891b2'];

const eur0 = (v) => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(v || 0);

const dayLabel = (ymd) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Amsterdam' });
};

// Datum voor de prognosetabel: weekdag + dd-mm als losse delen, zodat de
// datum kolom-strak uitlijnt (weekdag-afkortingen zijn proportioneel breed)
const dayParts = (ymd) => {
  const d = new Date(`${ymd}T12:00:00Z`);
  const wd = d.toLocaleDateString('nl-NL', { weekday: 'short', timeZone: 'Europe/Amsterdam' });
  const [, m, day] = ymd.split('-');
  return { wd, dm: `${day}-${m}` };
};

const hhmm = (t) => (t ? String(t).substring(0, 5) : null);

// Minuten -> duur als UU:MM: 215 -> "03:35", 45 -> "00:45"
const fmtDur = (min) => {
  const m = Math.round(min || 0);
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

// Bezettings-kleur: >100% overboekt (rood), 85-100 vol (oranje), anders groen
const utilColor = (u) => {
  if (u == null) return 'var(--color-neutral-400)';
  if (u > 100) return '#dc2626';
  if (u >= 85) return '#d97706';
  return '#059669';
};

// Tooltip die uitlegt wat het bezettingspercentage betekent
const utilTitle = (r) => {
  if (!r || r.utilization == null) return undefined;
  const status = r.utilization > 100 ? 'Overboekt: route langer dan de werkdag.'
    : r.utilization >= 85 ? 'Dag zit vol.'
    : 'Ruimte om bij te plannen.';
  const eind = r.plannedEnd ? `, thuis ${hhmm(r.plannedEnd)}` : '';
  return `Bezetting ${r.utilization}%: route van ${fmtDur(r.plannedMinutes)} `
    + `(${fmtDur(r.workMinutes)} klussen + ${fmtDur(r.travelMinutes)} rijden${eind}) `
    + `in een werkvenster van ${fmtDur(r.windowMinutes)} (${hhmm(r.startTime)}–${hhmm(r.endTime)}). ${status}`;
};

const RouteMap = ({ day, driverColor, isDark }) => {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return undefined;
    if (leafletRef.current) { leafletRef.current.remove(); leafletRef.current = null; }
    const map = L.map(mapRef.current, {
      center: [52.2, 5.5],
      zoom: 7,
      minZoom: 7,
      maxZoom: 13,
      maxBounds: [[50.5, 2.8], [53.8, 7.6]],
      maxBoundsViscosity: 1.0,
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: false,
    });
    const tiles = isDark
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    L.tileLayer(tiles, { maxZoom: 18 }).addTo(map);
    leafletRef.current = map;
    return () => { map.remove(); leafletRef.current = null; };
  }, [isDark]);

  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => map.removeLayer(m));
    markersRef.current = [];
    if (!day) return;

    // Zoom fit alleen op de KLUSSEN van de dag; start/eind (thuisadres, vaak
    // Friesland) wordt wel getekend maar rekt de kaart niet meer op.
    const bounds = [];
    const homeBounds = [];
    day.routes.forEach((route) => {
      const color = driverColor(route.driver);

      // Start-/eindpunt (thuis, of hotel bij overnachting). Zelfde locatie ->
      // één gecombineerde marker.
      const sameSpot = route.startPoint && route.endPoint
        && route.startPoint.lat === route.endPoint.lat
        && route.startPoint.lng === route.endPoint.lng;
      const pointMarker = (pt, label, title, timeLabel) => {
        if (!pt || pt.lat == null) return;
        const w = label.length > 1 ? 30 : 24;
        const icon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: ${w}px; height: 24px; border-radius: 6px;
            background: ${color}; color: white;
            display: flex; align-items: center; justify-content: center;
            font-weight: 800; font-size: 10px; letter-spacing: 0;
            border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          ">${label}</div>`,
          iconSize: [w, 24],
          iconAnchor: [w / 2, 12],
        });
        const m = L.marker([pt.lat, pt.lng], { icon, zIndexOffset: 100 }).addTo(map);
        m.bindPopup(`
          <div style="font-size:13px; line-height:1.5; min-width: 160px;">
            <strong>${title} · ${route.driver}</strong><br/>
            ${pt.addressLine ? pt.addressLine + ', ' : ''}${pt.city || ''}<br/>
            ${timeLabel || ''}
          </div>
        `);
        markersRef.current.push(m);
        homeBounds.push([pt.lat, pt.lng]);
      };
      const vertrek = route.startPoint && route.startPoint.time ? `vertrek ${hhmm(route.startPoint.time)}` : null;
      const terug = route.endPoint && route.endPoint.time ? `terug ${hhmm(route.endPoint.time)}` : null;
      if (sameSpot) {
        pointMarker(route.startPoint, 'S/E', 'Start & eind', [vertrek, terug].filter(Boolean).join(' · '));
      } else {
        // Verschillend start- en eindpunt (bv. hotel bij overnachting):
        // twee losse markers op hun eigen locatie
        pointMarker(route.startPoint, 'S', 'Start', vertrek);
        pointMarker(route.endPoint, 'E', 'Einde', terug);
      }

      route.stops.forEach((stop) => {
        if (stop.lat == null || stop.lng == null) return;
        const icon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="
            width: 26px; height: 26px; border-radius: 50%;
            background: ${stop.delivered ? color : 'white'};
            border: 2.5px solid ${color};
            color: ${stop.delivered ? 'white' : color};
            display: flex; align-items: center; justify-content: center;
            font-weight: 700; font-size: 11px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.25);
          ">${stop.delivered ? '✓' : stop.orderIndex}</div>`,
          iconSize: [26, 26],
          iconAnchor: [13, 13],
        });
        const marker = L.marker([stop.lat, stop.lng], { icon }).addTo(map);
        marker.bindPopup(`
          <div style="font-size:13px; line-height:1.5; min-width: 180px;">
            <strong>${stop.customer || stop.city || 'Stop ' + stop.orderIndex}</strong><br/>
            ${stop.city || ''} · ${route.driver}<br/>
            ${stop.service ? stop.service + '<br/>' : ''}
            ETA ${hhmm(stop.eta) || '—'} · ${stop.durationMinutes} min · <strong>${eur0(stop.price)}</strong><br/>
            ${stop.delivered ? '✅ afgerond' : '🕐 gepland'}
          </div>
        `);
        markersRef.current.push(marker);
        bounds.push([stop.lat, stop.lng]);
      });
    });
    const fitTo = bounds.length > 0 ? bounds : homeBounds;
    if (fitTo.length > 0) {
      map.fitBounds(fitTo, { padding: [24, 24], maxZoom: 11 });
    }
    // isDark in deps: de kaart wordt bij thema-wissel opnieuw aangemaakt,
    // dus de markers moeten dan ook opnieuw geplaatst worden
  }, [day, driverColor, isDark]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: '360px',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        // tile-kleur als achtergrond (subpixel-kieren onzichtbaar)
        background: isDark ? '#090a0f' : '#fafaf8',
        position: 'relative',
        isolation: 'isolate',
        zIndex: 0,
      }}
    />
  );
};

const RoutesSection = ({ data, loading, isDark = false }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  // Chauffeur -> kleur, stabiel over alle dagen
  const driverColor = useMemo(() => {
    const map = {};
    let i = 0;
    (data?.days || []).forEach((d) => d.routes.forEach((r) => {
      if (!(r.driver in map)) { map[r.driver] = DRIVER_COLORS[i % DRIVER_COLORS.length]; i += 1; }
    }));
    return (driver) => map[driver] || 'var(--color-neutral-500)';
  }, [data]);

  const days = useMemo(() => data?.days || [], [data]);

  // Vaste kolomvolgorde: monteurs op volgorde van eerste verschijning
  const drivers = useMemo(() => {
    const seen = [];
    days.forEach((d) => d.routes.forEach((r) => {
      if (!seen.includes(r.driver)) seen.push(r.driver);
    }));
    return seen;
  }, [days]);
  // Kaart toont: gekozen dag, anders vandaag, anders de eerstvolgende dag met routes
  const mapDay = useMemo(() => {
    if (!days.length) return null;
    if (selectedDate) return days.find((d) => d.date === selectedDate) || null;
    return days.find((d) => d.date === data.today) || days[0];
  }, [days, selectedDate, data]);

  if (loading && !data) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }} className="lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div key={i} className="card-blue">
            <div className="card-body">
              <div style={{ height: '360px', background: 'var(--color-neutral-100)', borderRadius: 'var(--radius-md)' }} className="animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!data || days.length === 0) return null;

  const isToday = mapDay && mapDay.date === data.today;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(1, 1fr)', gap: 'var(--space-6)', marginBottom: 'var(--space-6)' }} className="lg:grid-cols-2">
      {/* Routekaart */}
      <div className="card-blue">
        <div className="card-header">
          <h3 className="card-title">
            {isToday ? 'Routes vandaag' : `Routes ${mapDay ? dayLabel(mapDay.date) : ''}`}
          </h3>
        </div>
        <div className="card-body">
          {mapDay ? (
            <>
              <RouteMap day={mapDay} driverColor={driverColor} isDark={isDark} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 'var(--space-3)' }}>
                {mapDay.routes.map((r) => (
                  <div key={r.driver + r.licencePlate} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.82rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: driverColor(r.driver), flexShrink: 0 }} />
                    <strong style={{ minWidth: '64px' }}>{r.driver}</strong>
                    <span style={{ color: 'var(--color-neutral-500)' }}>
                      {r.deliveredCount}/{r.stops.length} stops
                      {' · '}{hhmm(r.plannedStart) || hhmm(r.startTime)}–{hhmm(r.plannedEnd) || hhmm(r.endTime)}
                      {' · '}{fmtDur(r.workMinutes)} klus + {fmtDur(r.travelMinutes)} rijden
                    </span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700 }}>{eur0(r.value)}</span>
                    {r.utilization != null && (
                      <span
                        title={utilTitle(r)}
                        style={{ color: utilColor(r.utilization), fontWeight: 600, minWidth: '44px', textAlign: 'right', cursor: 'help' }}
                      >
                        {r.utilization}% bezet
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ color: 'var(--color-neutral-500)', fontSize: '0.875rem' }}>Geen routes gepland.</p>
          )}
        </div>
      </div>

      {/* Omzetprognose vooruit — tabel: datum | kolom per monteur | totaal */}
      <div className="card-blue">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="card-title">Omzetprognose planning</h3>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.72rem', color: 'var(--color-neutral-500)', whiteSpace: 'nowrap' }}>
            {days.length} dagen
            <InfoDot text="Geplande omzet per dag per monteur uit RouteLogic (komende 90 dagen). Het percentage is de bezetting: routeduur (vertrek t/m thuiskomst) t.o.v. het werkvenster — groen = ruimte om bij te plannen, oranje = vol, rood = overboekt. Klik op een dag om die route op de kaart te tonen." />
          </span>
        </div>
        <div style={{ maxHeight: '460px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                <th style={{ textAlign: 'left', padding: '8px 12px 8px 16px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  DATUM
                </th>
                {drivers.map((name) => (
                  <th key={name} style={{ textAlign: 'right', padding: '8px 12px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-600)', borderBottom: '1px solid var(--color-neutral-200)', whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: driverColor(name), marginRight: 6, verticalAlign: 'baseline' }} />
                    {name.toUpperCase()}
                  </th>
                ))}
                <th style={{ textAlign: 'right', padding: '8px 16px 8px 12px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', color: 'var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  TOTAAL
                </th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const dayTotal = d.routes.reduce((s, r) => s + r.value, 0);
                const active = mapDay && mapDay.date === d.date;
                const isToday = d.date === data.today;
                const byDriver = Object.fromEntries(d.routes.map((r) => [r.driver, r]));
                return (
                  <tr
                    key={d.date}
                    onClick={() => setSelectedDate(d.date === selectedDate ? null : d.date)}
                    style={{
                      cursor: 'pointer',
                      background: active
                        ? (isDark ? 'rgba(59, 130, 246, 0.18)' : 'var(--color-blue-50)')
                        : 'transparent',
                      borderBottom: '1px solid var(--color-neutral-100)',
                    }}
                  >
                    <td
                      title={isToday ? 'Vandaag' : undefined}
                      style={{ padding: '9px 12px 9px 16px', fontSize: '0.8rem', whiteSpace: 'nowrap', fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--color-blue-500)' : 'var(--color-neutral-800)' }}
                    >
                      <span style={{ display: 'inline-block', width: '2.1em' }}>{dayParts(d.date).wd}</span>
                      {dayParts(d.date).dm}
                    </td>
                    {drivers.map((name) => {
                      const r = byDriver[name];
                      return (
                        <td key={name} style={{ textAlign: 'right', padding: '9px 12px', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {r ? (
                            <>
                              <span style={{ fontWeight: 600 }}>{eur0(r.value)}</span>
                              {r.utilization != null && (
                                <span
                                  title={utilTitle(r)}
                                  style={{ color: utilColor(r.utilization), fontWeight: 600, fontSize: '0.72rem', marginLeft: 6, display: 'inline-block', minWidth: '38px', textAlign: 'right', cursor: 'help' }}
                                >
                                  {r.utilization}%
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: 'var(--color-neutral-300)' }}>—</span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'right', padding: '9px 16px 9px 12px', fontSize: '0.8rem', fontWeight: 700 }}>
                      {eur0(dayTotal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RoutesSection;
